import React from 'react';
import { motion } from 'framer-motion';
import { BiBot } from 'react-icons/bi';
import { Sparkles, Zap } from 'lucide-react';

export const Hero: React.FC = () => {
  const [animatedBots, setAnimatedBots] = React.useState<number[]>([]);

  React.useEffect(() => {
    const interval = setInterval(() => {
      setAnimatedBots((prev) => {
        const newBot = Math.floor(Math.random() * 6);
        return [...prev.slice(-2), newBot];
      });
    }, 2000);
    return () => clearInterval(interval);
  }, []);

  return (
    <section className="relative min-h-screen flex items-center justify-center overflow-hidden pt-20">
      {/* Animated Background Particles */}
      <div className="absolute inset-0">
        {[...Array(50)].map((_, i) => (
          <motion.div
            key={i}
            className="absolute w-1 h-1 bg-purple-500 rounded-full"
            style={{
              left: `${Math.random() * 100}%`,
              top: `${Math.random() * 100}%`,
            }}
            animate={{
              opacity: [0.2, 0.8, 0.2],
              scale: [1, 1.5, 1],
            }}
            transition={{
              duration: 3 + Math.random() * 2,
              repeat: Infinity,
              delay: Math.random() * 2,
            }}
          />
        ))}
      </div>

      {/* Floating Background Bots */}
      <div className="fixed inset-0 overflow-hidden pointer-events-none">
        {[...Array(6)].map((_, i) => (
          <motion.div
            key={i}
            className="absolute"
            style={{
              left: `${15 + i * 15}%`,
              top: `${10 + (i % 3) * 25}%`,
            }}
            animate={
              animatedBots.includes(i)
                ? {
                    scale: [1, 1.2, 1],
                    rotate: [0, 10, -10, 0],
                  }
                : {}
            }
            transition={{ duration: 0.5 }}
          >
            <motion.div
              animate={{
                y: [0, -10, 0],
              }}
              transition={{
                duration: 3 + i,
                repeat: Infinity,
                repeatType: 'reverse',
              }}
              className="opacity-10"
            >
              <BiBot size={60 + i * 10} className="text-purple-500" />
            </motion.div>
            {animatedBots.includes(i) && (
              <motion.div
                initial={{ scale: 0, opacity: 1 }}
                animate={{ scale: 2, opacity: 0 }}
                transition={{ duration: 1 }}
                className="absolute inset-0 flex items-center justify-center"
              >
                <div className="w-full h-full rounded-full bg-purple-500" />
              </motion.div>
            )}
          </motion.div>
        ))}
      </div>

      {/* Gradient Overlay */}
      <div className="absolute inset-0 bg-gradient-to-br from-purple-900/20 via-pink-900/20 to-blue-900/20" />

      {/* Content */}
      <div className="relative z-10 container mx-auto px-6 text-center">
        {/* Badge */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-purple-500/10 border border-purple-500/20 text-purple-300 mb-8"
        >
          <Sparkles size={16} />
          <span className="text-sm font-medium">Now with GPT-4 & Claude Integration</span>
        </motion.div>

        {/* Headline */}
        <motion.h1
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.2 }}
          className="text-5xl md:text-7xl lg:text-8xl font-extrabold mb-6 leading-tight"
          style={{ fontFamily: "'Space Grotesk', 'Inter', sans-serif" }}
        >
          <span className="bg-gradient-to-r from-purple-400 via-pink-400 to-blue-400 bg-clip-text text-transparent">
            Build AI Assistants
          </span>
          <br />
          <span className="text-white">That Actually Work</span>
        </motion.h1>

        {/* Subheadline */}
        <motion.p
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.4 }}
          className="text-xl md:text-2xl text-gray-300 mb-12 max-w-3xl mx-auto leading-relaxed"
          style={{ fontFamily: "'DM Sans', 'Inter', sans-serif" }}
        >
          Create intelligent, multi-channel AI assistants in minutes with our
          no-code visual flow builder. No coding required, infinite possibilities.
        </motion.p>

        {/* CTA Buttons */}
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ delay: 0.6, duration: 0.5 }}
          className="flex flex-col sm:flex-row gap-4 justify-center mb-16"
        >
          <motion.a
            href="/register"
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            className="group relative px-8 py-4 rounded-lg bg-gradient-to-r from-purple-500 to-pink-500 text-white font-semibold text-lg shadow-2xl shadow-purple-500/50 overflow-hidden"
          >
            <span className="relative z-10 flex items-center justify-center gap-2">
              <Zap size={20} />
              Start Building Free
            </span>
            <div className="absolute inset-0 bg-gradient-to-r from-purple-600 to-pink-600 opacity-0 group-hover:opacity-100 transition-opacity" />
            {/* Shimmer effect */}
            <div className="absolute inset-0 overflow-hidden">
              <div className="absolute inset-0 translate-x-[-100%] bg-gradient-to-r from-transparent via-white/30 to-transparent group-hover:translate-x-[100%] transition-transform duration-1000" />
            </div>
          </motion.a>
          <motion.a
            href="#demo"
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            className="px-8 py-4 rounded-lg border-2 border-purple-500 text-white hover:bg-purple-500/10 transition-all text-lg font-semibold"
          >
            Watch 2-Min Demo
          </motion.a>
        </motion.div>

        {/* 3D Bot Animation Placeholder */}
        <motion.div
          initial={{ opacity: 0, y: 100 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.8, duration: 1, type: 'spring' }}
          className="relative max-w-4xl mx-auto"
        >
          <div className="relative rounded-2xl overflow-hidden bg-gradient-to-br from-purple-500/10 to-pink-500/10 backdrop-blur-xl border border-purple-500/20 p-8 md:p-12">
            {/* Animated Bot Icon */}
            <motion.div
              animate={{
                y: [0, -20, 0],
                rotate: [0, 5, -5, 0],
              }}
              transition={{
                duration: 5,
                repeat: Infinity,
                repeatType: 'reverse',
              }}
              className="flex items-center justify-center"
            >
              <div className="relative">
                <motion.div
                  animate={{
                    boxShadow: [
                      '0 0 20px rgba(139, 92, 246, 0.5)',
                      '0 0 60px rgba(139, 92, 246, 0.8)',
                      '0 0 20px rgba(139, 92, 246, 0.5)',
                    ],
                  }}
                  transition={{ duration: 2, repeat: Infinity }}
                  className="rounded-full bg-gradient-to-br from-purple-500 to-pink-500 p-8"
                >
                  <BiBot size={120} className="text-white" />
                </motion.div>
                {/* Orbiting particles */}
                {[...Array(3)].map((_, i) => (
                  <motion.div
                    key={i}
                    className="absolute w-4 h-4 bg-blue-400 rounded-full"
                    style={{
                      top: '50%',
                      left: '50%',
                    }}
                    animate={{
                      x: [
                        0,
                        Math.cos((i * 2 * Math.PI) / 3) * 100,
                        Math.cos((i * 2 * Math.PI) / 3 + Math.PI) * 100,
                        0,
                      ],
                      y: [
                        0,
                        Math.sin((i * 2 * Math.PI) / 3) * 100,
                        Math.sin((i * 2 * Math.PI) / 3 + Math.PI) * 100,
                        0,
                      ],
                    }}
                    transition={{
                      duration: 3,
                      repeat: Infinity,
                      delay: i * 0.3,
                    }}
                  />
                ))}
              </div>
            </motion.div>

            {/* Feature Tags */}
            <div className="mt-12 flex flex-wrap justify-center gap-3">
              {['Voice AI', 'SMS', 'Web Chat', 'WhatsApp', 'Real-time Analytics'].map(
                (tag, i) => (
                  <motion.div
                    key={tag}
                    initial={{ opacity: 0, scale: 0.8 }}
                    animate={{ opacity: 1, scale: 1 }}
                    transition={{ delay: 1 + i * 0.1 }}
                    className="px-4 py-2 rounded-full bg-gray-800/80 backdrop-blur-sm border border-gray-700 text-gray-300 text-sm font-medium"
                  >
                    {tag}
                  </motion.div>
                )
              )}
            </div>
          </div>
        </motion.div>

      </div>
    </section>
  );
};
